import { OAuthProvider } from '@seapoint/server/enums';
import { Dot } from 'lucide-react';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { cn } from '../../../utils/cn';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { OAuthProviderLogoIcon } from './oauth-provider-logo-icon';

export type AllowedOAuthProvider = OAuthProvider.GMAIL | OAuthProvider.XERO;

type OAuthConnectionBadgeProps = {
  provider: AllowedOAuthProvider;
  isConnected: boolean;
  isLoading: boolean;
  onConnect: () => void;
};

export const OAuthConnectionBadge = ({ provider, isConnected, isLoading, onConnect }: OAuthConnectionBadgeProps) => {
  const { connectedBadgeText, disconnectedBadgeText } = getBadgeContent(provider);

  if (!isConnected) {
    return (
      <Button
        variant="secondary"
        onClick={onConnect}
        iconLeft={<OAuthProviderLogoIcon provider={provider} />}
        isLoading={isLoading}
      >
        {isLoading ? 'Checking connection...' : disconnectedBadgeText}
      </Button>
    );
  }

  return (
    <Inline
      space={isLoading ? 2 : 0}
      className={cn('bg-seapoint-green-background w-max rounded-full bg-seapoint-gray-background px-4 py-1.5', {
        'pl-2.5': !isLoading,
      })}
    >
      <div data-testid="connection-status-icon" className="h-7 w-7">
        <Dot color="#169B62" className="h-full w-full" />
      </div>

      <p
        className={cn('text-xs font-medium leading-6 tracking-tight text-black', {
          'text-seapoint-gray': isLoading,
        })}
      >
        {isLoading ? 'Checking connection...' : connectedBadgeText}
      </p>
    </Inline>
  );
};

const getBadgeContent = (provider: AllowedOAuthProvider) => {
  switch (provider) {
    case OAuthProvider.GMAIL:
      return {
        connectedBadgeText: 'Gmail Connection Active',
        disconnectedBadgeText: 'Connect Gmail',
      };
    case OAuthProvider.XERO:
      return {
        connectedBadgeText: 'Xero Connection Active',
        disconnectedBadgeText: 'Connect Xero',
      };
    default:
      return exhaustiveCheck(provider);
  }
};
